import { ErrorMessage } from '@hookform/error-message';
import { ReactNode } from 'react';
import { Control, FieldPathByValue, FieldValues, useController } from 'react-hook-form';

import { Form } from '../../../../bootstrap';

type Props<T extends FieldValues, V extends string> = {
  fieldName: FieldPathByValue<T, V[]>;
  control: Control<T>;
  options: { value: V; label: ReactNode }[];
  label?: ReactNode;
  hideErrorMessage?: boolean;
  disabled?: boolean;
};

export function ControlledMultiSelectCheckbox<T extends FieldValues, V extends string>({
  fieldName,
  control,
  options,
  label,
  hideErrorMessage,
  disabled,
}: Props<T, V>) {
  const {
    field,
    formState: { errors },
  } = useController({ name: fieldName, control });

  const selectedValues: V[] = field.value ?? [];

  const handleChange = (optionValue: V, checked: boolean) => {
    if (checked) {
      field.onChange([...selectedValues, optionValue]);
    } else {
      field.onChange(selectedValues.filter((v) => v !== optionValue));
    }
  };

  return (
    <>
      {label && <Form.Label className="u-fs-2 fw-bold">{label}</Form.Label>}
      <div>
        {options.map((option) => (
          <Form.Check key={option.value} id={`${String(fieldName)}-${option.value}`} type="checkbox" className="pb-2">
            <Form.Check.Input
              type="checkbox"
              checked={selectedValues.includes(option.value)}
              onChange={(e) => handleChange(option.value, e.target.checked)}
              onBlur={field.onBlur}
              disabled={disabled}
            />
            <Form.Check.Label className="u-pointer">{option.label}</Form.Check.Label>
          </Form.Check>
        ))}
      </div>
      {!hideErrorMessage && (
        <ErrorMessage
          errors={errors}
          name={fieldName as any}
          render={({ message }) => <div className="u-fs-2 u-color-red1">{message}</div>}
        />
      )}
    </>
  );
}
